import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminOrders } from '../../utils/api';

const StatCard = ({ icon, label, value, sub, color }) => (
  <div className="stat-card" style={{ '--accent': color }}>
    <div className="stat-card__icon">{icon}</div>
    <div>
      <p className="stat-card__value">{value}</p>
      <p className="stat-card__label">{label}</p>
      {sub && <p className="stat-card__sub">{sub}</p>}
    </div>
  </div>
);

const STATUS_COLORS = {
  pending: '#f6ad55', confirmed: '#68d391', processing: '#76e4f7',
  shipped: '#9f7aea', delivered: '#48bb78', cancelled: '#fc8181',
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminOrders.getDashboard().then(setStats).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;

  return (
    <div className="admin-dashboard">
      <div className="admin-page-header">
        <h2>Dashboard</h2>
        <p>Welcome back! Here's what's happening with Qaya KW.</p>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <StatCard icon="💰" label="Total Revenue" value={`KD ${parseFloat(stats?.revenue || 0).toFixed(3)}`} color="#D4943A" />
        <StatCard icon="📦" label="Total Orders" value={stats?.total_orders || 0} color="#68d391" />
        <StatCard icon="⏳" label="Pending Orders" value={stats?.pending_orders || 0} sub="Needs attention" color="#f6ad55" />
        <StatCard icon="👥" label="Customers" value={stats?.total_customers || 0} color="#9f7aea" />
      </div>

      <div className="dashboard-bottom">
        {/* Recent Orders */}
        <div className="admin-card">
          <div className="admin-card__header">
            <h3>Recent Orders</h3>
            <Link to="/admin/orders">View All →</Link>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr><th>Order #</th><th>Customer</th><th>Total</th><th>Status</th><th>Date</th></tr>
              </thead>
              <tbody>
                {stats?.recent_orders?.map(o => (
                  <tr key={o.id}>
                    <td><Link to={`/admin/orders/${o.id}`} className="table-link">{o.order_number}</Link></td>
                    <td>{o.shipping_full_name || o.guest_email || 'Guest'}</td>
                    <td><strong>KD {parseFloat(o.total).toFixed(3)}</strong></td>
                    <td>
                      <span className="status-badge" style={{ background: STATUS_COLORS[o.status] + '22', color: STATUS_COLORS[o.status] }}>
                        {o.status}
                      </span>
                    </td>
                    <td>{new Date(o.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Products */}
        <div className="admin-card">
          <div className="admin-card__header">
            <h3>Top Products</h3>
            <Link to="/admin/products">View All →</Link>
          </div>
          <div className="top-products">
            {stats?.top_products?.map((p, i) => (
              <div key={i} className="top-product-row">
                <span className="top-product-rank">{i + 1}</span>
                <div className="top-product-info">
                  <p>{p.name}</p>
                  <small>{p.sold} units sold</small>
                </div>
                <strong>KD {parseFloat(p.revenue || 0).toFixed(3)}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .admin-page-header { margin-bottom: 28px; }
        .admin-page-header h2 { font-family: var(--font-serif); font-size: 1.6rem; }
        .admin-page-header p { color: var(--text-muted); margin-top: 4px; }
        .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 28px; }
        .stat-card {
          background: white; border-radius: var(--radius-lg); padding: 24px; display: flex; gap: 16px; align-items: center;
          border-left: 4px solid var(--accent); box-shadow: var(--shadow-sm);
        }
        .stat-card__icon { font-size: 2rem; }
        .stat-card__value { font-size: 1.6rem; font-weight: 700; }
        .stat-card__label { font-size: 0.82rem; color: var(--text-muted); }
        .stat-card__sub { font-size: 0.75rem; color: var(--accent); }
        .dashboard-bottom { display: grid; grid-template-columns: 1.5fr 1fr; gap: 24px; }
        .admin-card { background: white; border-radius: var(--radius-lg); padding: 24px; box-shadow: var(--shadow-sm); }
        .admin-card__header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
        .admin-card__header h3 { font-family: var(--font-serif); }
        .admin-card__header a { font-size: 0.85rem; color: var(--gold); }
        .admin-table-wrap { overflow-x: auto; }
        .admin-table { width: 100%; border-collapse: collapse; }
        .admin-table th { text-align: left; padding: 10px 12px; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-muted); border-bottom: 1px solid var(--border); }
        .admin-table td { padding: 12px; font-size: 0.88rem; border-bottom: 1px solid #f5f5f5; }
        .table-link { color: var(--gold); font-weight: 600; }
        .status-badge { padding: 4px 10px; border-radius: 50px; font-size: 0.75rem; font-weight: 600; text-transform: capitalize; }
        .top-products { display: flex; flex-direction: column; gap: 14px; }
        .top-product-row { display: flex; align-items: center; gap: 12px; }
        .top-product-rank { width: 24px; height: 24px; background: var(--cream); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: 700; }
        .top-product-info { flex: 1; }
        .top-product-info p { font-size: 0.88rem; font-weight: 500; }
        .top-product-info small { font-size: 0.75rem; color: var(--text-muted); }
        @media (max-width: 1024px) { .stats-grid { grid-template-columns: repeat(2, 1fr); } .dashboard-bottom { grid-template-columns: 1fr; } }
        @media (max-width: 480px) { .stats-grid { grid-template-columns: 1fr 1fr; } }
      `}</style>
    </div>
  );
};

export default AdminDashboard;
